import { useState, useEffect } from 'react';
import { Outlet, Link, useLocation, useNavigate } from 'react-router-dom';
import { useAuth } from '../../context/AuthContext';
import axios from 'axios';
import { 
  LayoutDashboard, 
  Palette, 
  Image, 
  FileText, 
  Users, 
  Sparkles, 
  CreditCard, 
  Settings, 
  LogOut,
  Menu,
  X,
  ExternalLink
} from 'lucide-react';
import { Button } from '../../components/ui/button';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const DashboardLayout = () => {
  const { user, logout, getAuthHeader } = useAuth();
  const location = useLocation();
  const navigate = useNavigate();
  const [studio, setStudio] = useState(null);
  const [sidebarOpen, setSidebarOpen] = useState(false);

  useEffect(() => {
    const fetchStudio = async () => {
      try {
        const response = await axios.get(`${API}/studios/me`, {
          headers: getAuthHeader()
        });
        setStudio(response.data);
      } catch (error) {
        // No studio yet - redirect to onboarding
        if (location.pathname !== '/app/onboarding') {
          navigate('/app/onboarding');
        }
      }
    };
    fetchStudio();
  }, [getAuthHeader, location.pathname, navigate]);

  const handleLogout = () => {
    logout();
    navigate('/');
  };

  const navItems = [
    { href: '/app', icon: LayoutDashboard, label: 'Overview', exact: true },
    { href: '/app/wraps', icon: Palette, label: 'Wraps' },
    { href: '/app/branding', icon: Image, label: 'Branding' },
    { href: '/app/pages', icon: FileText, label: 'Pages' },
    { href: '/app/gallery', icon: Image, label: 'Gallery' },
    { href: '/app/leads', icon: Users, label: 'Leads' },
    { href: '/app/renders', icon: Sparkles, label: 'Renders' },
    { href: '/app/billing', icon: CreditCard, label: 'Billing' },
    { href: '/app/settings', icon: Settings, label: 'Settings' },
  ];

  const isActive = (href, exact = false) => {
    if (exact) return location.pathname === href;
    return location.pathname.startsWith(href);
  };

  return (
    <div className="min-h-screen bg-[#050505]" data-testid="dashboard-layout">
      {/* Mobile Header */}
      <header className="md:hidden fixed top-0 left-0 right-0 z-50 bg-[#0a0a0a] border-b border-white/10 px-4 h-16 flex items-center justify-between">
        <Link to="/app" className="font-bold text-xl text-gold-gradient flex items-center gap-2">
          WrapViz
        </Link>
        <button onClick={() => setSidebarOpen(!sidebarOpen)}>
          {sidebarOpen ? <X size={24} /> : <Menu size={24} />}
        </button>
      </header>

      {/* Sidebar */}
      <aside className={`
        fixed top-0 left-0 h-full w-64 bg-[#0a0a0a] border-r border-white/10 z-40
        transform transition-transform duration-300
        ${sidebarOpen ? 'translate-x-0' : '-translate-x-full'}
        md:translate-x-0
      `}>
        <div className="p-6 border-b border-white/10">
          <Link to="/app" className="font-bold text-2xl text-gold-gradient flex items-center gap-2">
            WrapViz
          </Link>
        </div>

        <nav className="p-4 space-y-1">
          {navItems.map((item) => (
            <Link
              key={item.href}
              to={item.href}
              onClick={() => setSidebarOpen(false)}
              className={`flex items-center gap-3 px-4 py-3 rounded-sm transition-colors ${
                isActive(item.href, item.exact)
                  ? 'bg-[#FFFFFF] text-black'
                  : 'text-white/60 hover:text-white hover:bg-white/5'
              }`}
              data-testid={`nav-${item.label.toLowerCase()}`}
            >
              <item.icon size={18} />
              <span className="font-medium">{item.label}</span>
            </Link>
          ))}
        </nav>

        {/* Studio Link */}
        {studio && (
          <div className="absolute bottom-24 left-0 right-0 px-4">
            <a
              href={`/s/${studio.slug}`}
              target="_blank"
              rel="noopener noreferrer"
              className="flex items-center gap-2 px-4 py-3 text-white/40 hover:text-white text-sm"
            >
              <ExternalLink size={16} />
              View Public Site
            </a>
          </div>
        )}

        {/* User Menu */}
        <div className="absolute bottom-0 left-0 right-0 p-4 border-t border-white/10">
          <div className="flex items-center gap-3 mb-4">
            <div className="w-10 h-10 rounded-full bg-[#FFFFFF] flex items-center justify-center text-black font-bold">
              {user?.name?.charAt(0).toUpperCase()}
            </div>
            <div className="flex-1 min-w-0">
              <p className="font-medium truncate">{user?.name}</p>
              <p className="text-xs text-white/40 truncate">{user?.email}</p>
            </div>
          </div>
          <Button
            variant="ghost"
            className="w-full justify-start text-white/60 hover:text-white"
            onClick={handleLogout}
            data-testid="logout-btn"
          >
            <LogOut size={18} className="mr-2" />
            Logout
          </Button>
        </div>
      </aside>

      {/* Main Content */}
      <main className="md:ml-64 min-h-screen pt-16 md:pt-0">
        <div className="p-6 md:p-8">
          <Outlet context={{ studio, setStudio }} />
        </div>
      </main>

      {/* Mobile Sidebar Overlay */}
      {sidebarOpen && (
        <div 
          className="md:hidden fixed inset-0 bg-black/50 z-30"
          onClick={() => setSidebarOpen(false)}
        ></div>
      )}
    </div>
  );
};

export default DashboardLayout;
